import { useMemo, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faUser,
  faBook,
  faNewspaper,
  faComment,
  faEye,
  faLock,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "../../util/apiClient";
import { forumAuthorDisplay, listPosts } from "../../util/forumApi";
import { profilePath } from "../../util/urlIds";
import { logger } from "../../util/logger";
import { avatarSrc } from "../../util/avatar";
import BookCover from "../Shared/BookCover";
import LocaleFilterChips from "../Shared/LocaleFilterChips";
import { useT, useI18n } from "../../i18n/I18nContext";

// In-app search surface mounted by ``Navigation``. Three sections —
// writers, books (public projects), forum posts — each filtered
// case-insensitively against the query the navbar input drives.
const Search = ({ searchQuery }) => {
  const t = useT();
  const { locale } = useI18n();
  const currentLocale = locale === "fr" ? "fr" : "en";
  const navigate = useNavigate();
  const selfEmail = (sessionStorage.getItem("email") || "").toLowerCase();
  const [users, setUsers] = useState([]);
  const [books, setBooks] = useState([]);
  const [posts, setPosts] = useState([]);
  // Locale filter shared by the books + forum sections. Writers
  // stay unfiltered — a profile is a profile regardless of which
  // language the writer publishes in.
  const [localeFilter, setLocaleFilter] = useState(currentLocale);

  useEffect(() => {
    // ``cancelled`` guards against late responses overwriting state
    // after the user has navigated away from /search. Without it,
    // the three independent fetches below resolve at unpredictable
    // times and a slow ``fetchPosts`` from a previous mount could
    // clobber the next page's state. All three setters short-
    // circuit through this flag.
    let cancelled = false;

    const fetchUsers = async () => {
      try {
        const { data } = await apiClient.post("/api/users/get/");
        if (cancelled) return;
        setUsers(data?.users_list ?? []);
      } catch (error) {
        if (cancelled) return;
        logger.error("search_users_failed", error?.message);
      }
    };

    const fetchBooks = async () => {
      // Two fetches in parallel:
      //   * ``visibility=public`` → every public project, from any
      //     author, so a reader can find published work.
      //   * default branch → the caller's own projects, *all
      //     visibilities*, so a writer can find their own drafts
      //     in the search bar (the previous public-only fetch
      //     hid private + shared rows from their own author —
      //     the bug surfaced as "I just created a project, why
      //     doesn't search find it?").
      // Merged + deduped by id so a public project the caller
      // also owns isn't double-rendered.
      const safeList = (resp) =>
        Array.isArray(resp?.data?.projects) ? resp.data.projects : [];
      const get = (params) =>
        apiClient
          .get("/api/projects/", { params })
          .catch((error) => {
            logger.warn("search_books_failed", {
              params,
              status: error?.response?.status,
              message: error?.message,
            });
            return null;
          });
      const [pubResp, ownResp] = await Promise.all([
        get({ visibility: "public", limit: 100 }),
        get({ limit: 100 }),
      ]);
      if (cancelled) return;
      const merged = new Map();
      for (const p of safeList(pubResp)) merged.set(p.id, p);
      for (const p of safeList(ownResp)) merged.set(p.id, p);
      setBooks(
        Array.from(merged.values()).map((p) => ({
          id: p.id,
          title: p.title || "",
          img: p.cover_url || "",
          description: p.description || "",
          // ``owner_username`` is embedded by the backend on every
          // project row, including for anonymous visitors — so the
          // book card can always show a real handle without having
          // to cross-reference the auth-gated /api/users/get/ list.
          ownerUsername: p.owner_username || p.owner_name || "",
          ownerEmail: p.owner_email || "",
          chapterCount: Number(p.published_chapter_count ?? p.chapter_count ?? 0),
          ratingAverage: Number(p.rating_average ?? 0),
          ratingCount: Number(p.rating_count ?? 0),
          readerCount: Number(p.reader_count ?? 0),
          visibility: p.visibility || "private",
          // Retained for locale filtering (the chip strip below).
          language: (p.language || "en").toLowerCase().slice(0, 2),
        })),
      );
    };

    const fetchPosts = async () => {
      try {
        const list = await listPosts({ limit: 50 });
        if (cancelled) return;
        setPosts(
          (list || []).map((p) => ({
            id: p.id,
            title: p.title || "",
            description: (p.body || "").slice(0, 200),
            author: forumAuthorDisplay(p),
            date: p.created_at
              ? new Date(p.created_at).toLocaleDateString()
              : "",
            // Forum's ``language`` field drives the chip filter
            // below. Falls back to en when the backend hasn't yet
            // shipped the new field (post-migration).
            language: (p.language || "en").toLowerCase().slice(0, 2),
          })),
        );
      } catch (error) {
        if (cancelled) return;
        logger.error("search_posts_failed", error?.message);
      }
    };

    fetchUsers();
    fetchBooks();
    fetchPosts();

    return () => {
      cancelled = true;
    };
  }, []);

  const q = (searchQuery || "").toLowerCase();
  const includes = (s) => (s || "").toString().toLowerCase().includes(q);

  // Locale-aware passthrough for content surfaces.
  const matchesLocale = (item) =>
    localeFilter === "all" ? true : (item.language || "en") === localeFilter;

  // Books match on title OR description so a writer searching for
  // "love" finds romances even when the title doesn't carry the
  // word. ``includes`` defends against missing fields.
  // Writers section deliberately bypasses the locale filter — a
  // writer's identity isn't language-bound.
  const filteredUsers = users.filter((u) => includes(u.name));
  const filteredBooks = books.filter(
    (b) => (includes(b.title) || includes(b.description)) && matchesLocale(b),
  );
  const filteredPosts = posts.filter(
    (p) => (includes(p.title) || includes(p.description)) && matchesLocale(p),
  );

  // Locale counts driving the chip badges — computed pre-search so
  // the badge values stay stable while the writer types.
  const localeCounts = useMemo(() => {
    const counts = { fr: 0, en: 0, all: 0 };
    for (const item of [...books, ...posts]) {
      const lang = (item.language || "en") === "fr" ? "fr" : "en";
      counts[lang] += 1;
      counts.all += 1;
    }
    return counts;
  }, [books, posts]);

  const renderUsers = (list) => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {list.map((user) => (
        <Link
          to={profilePath(user)}
          key={user.email || user.id}
          className="group block"
        >
          <div className="bg-surface rounded-xl border border-border p-4 hover:border-primary hover:shadow-md transition">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-16 h-16 rounded-full overflow-hidden border border-border bg-surfaceAlt">
                {avatarSrc(user.img) ? (
                  <img
                    src={avatarSrc(user.img)}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-secondText text-lg font-semibold">
                    {(user.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <p className="text-sm font-medium text-center text-text truncate w-full">
                {user.name}
              </p>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );

  // Visibility pill metadata (writer's own row only — reader-side
  // results are always public so a pill there would be noise).
  const visibilityPills = {
    public: {
      icon: faEye,
      label: t("profile.card.visibility.public"),
      tone: "bg-success/10 text-success border-success/30",
    },
    shared: {
      icon: faUsers,
      label: t("profile.card.visibility.shared"),
      tone: "bg-warning/10 text-warning border-warning/30",
    },
    private: {
      icon: faLock,
      label: t("profile.card.visibility.private"),
      tone: "bg-surfaceAlt text-secondText border-border",
    },
  };

  const renderBooks = (list) => (
    // Compact list-style row — small square cover + title +
    // author, nothing else. Dropped the engagement strip and
    // description so cards aren't elongated; less vertical
    // dominance per result, more results visible at a glance.
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {list.map((book) => {
        const isOwn =
          !!book.ownerEmail &&
          book.ownerEmail.toLowerCase() === selfEmail;
        const pill = visibilityPills[book.visibility] || visibilityPills.private;

        // Click target adapts: a writer's own private/shared
        // project → editor (the reader can't render private
        // chapters anyway). Everything else → reader.
        const handleClick = (e) => {
          if (isOwn && book.visibility !== "public") {
            e.preventDefault();
            sessionStorage.setItem("currentProjectId", String(book.id));
            navigate("/project");
          }
        };

        return (
          <Link
            to={`/read/${book.id}`}
            key={book.id}
            onClick={handleClick}
            className="group flex items-center gap-3 bg-surface rounded-xl border border-border p-3 hover:border-primary hover:shadow-md transition"
          >
            <div className="relative w-14 h-14 flex-shrink-0">
              <BookCover
                src={book.img}
                alt=""
                className="w-14 h-14 object-cover rounded-lg"
                iconClassName="text-secondText text-xl"
              />
              {/* Visibility pill — small dot in the corner for the
                  writer's own rows. Title attribute carries the
                  full label so the row stays compact. */}
              {isOwn ? (
                <span
                  className={`absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full border ${pill.tone}`}
                  title={pill.label}
                >
                  <FontAwesomeIcon icon={pill.icon} className="text-[8px]" />
                </span>
              ) : null}
            </div>
            <div className="flex-1 min-w-0">
              <h3
                className="text-sm font-semibold text-text truncate group-hover:text-primary transition"
                title={book.title}
              >
                {book.title}
              </h3>
              {/* Writer handle. ``ownerUsername`` comes straight
                  from the projects API (filled for anonymous
                  visitors too), so this works for unlogged users
                  too — previously the row cross-referenced
                  /api/users/get/ which is auth-gated, leaving
                  guests with the email's local part instead of
                  the actual handle. Email local part stays as a
                  last-resort fallback. */}
              {(book.ownerUsername || book.ownerEmail) ? (
                <p className="text-xs text-secondText truncate">
                  {book.ownerUsername ||
                    String(book.ownerEmail).split("@")[0]}
                </p>
              ) : null}
            </div>
          </Link>
        );
      })}
    </div>
  );

  const renderPosts = (list) => (
    <div className="grid grid-cols-1 gap-4">
      {list.map((post) => (
        <Link
          to={`/forum/${post.id}`}
          key={post.id}
          className="block bg-surface rounded-xl border border-border p-6 hover:border-primary hover:shadow-md transition"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-text mb-2 truncate">
                {post.title}
              </h3>
              {post.description ? (
                <p className="text-secondText line-clamp-2 mb-4">
                  {post.description}
                </p>
              ) : null}
              <div className="flex items-center gap-3 text-xs text-secondText">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5">
                  <FontAwesomeIcon icon={faComment} className="text-[10px]" />
                  {t("search.tag.discussion")}
                </span>
                {post.author ? <span className="truncate">{post.author}</span> : null}
                {post.date ? <span>{post.date}</span> : null}
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );

  const renderSection = (title, icon, content) => (
    <div className="mb-12">
      <div className="flex items-center gap-2 mb-6">
        <FontAwesomeIcon className="text-primary text-xl" icon={icon} />
        <h2 className="text-2xl font-semibold text-text">{title}</h2>
      </div>
      {content}
    </div>
  );

  return (
    <div className="py-10 px-4 lg:py-12 lg:px-8 max-w-7xl mx-auto">
      {/* Locale filter — applies to books + forum posts. Writers
          section ignores it (a profile is a profile regardless of
          language). Default = current UI locale so a FR reader's
          search results don't mix EN books they can't read. */}
      <div className="mb-6 flex justify-end">
        <LocaleFilterChips
          currentLocale={currentLocale}
          value={localeFilter}
          onChange={setLocaleFilter}
          counts={localeCounts}
        />
      </div>
      {searchQuery ? (
        <div className="space-y-12">
          {filteredUsers.length > 0 &&
            renderSection(
              t("search.section.users"),
              faUser,
              renderUsers(filteredUsers),
            )}
          {filteredBooks.length > 0 &&
            renderSection(
              t("search.section.books"),
              faBook,
              renderBooks(filteredBooks),
            )}
          {filteredPosts.length > 0 &&
            renderSection(
              t("search.section.posts"),
              faNewspaper,
              renderPosts(filteredPosts),
            )}
          {filteredUsers.length === 0 &&
            filteredBooks.length === 0 &&
            filteredPosts.length === 0 && (
              <div className="text-center py-12">
                <FontAwesomeIcon
                  className="text-secondText text-4xl mb-4"
                  icon={faSearch}
                />
                <p className="text-secondText text-lg">
                  {t("search.no_results", { query: searchQuery })}
                </p>
              </div>
            )}
        </div>
      ) : (
        <div className="space-y-12">
          {renderSection(t("search.section.users"), faUser, renderUsers(users))}
          {renderSection(t("search.section.books"), faBook, renderBooks(books))}
          {renderSection(t("search.section.posts"), faNewspaper, renderPosts(posts))}
        </div>
      )}
    </div>
  );
};

export default Search;
